import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Emoji, Menu, MenuItem } from '@gitbook/spine-react';

import type { LocalizedLanguagesList } from './types';

/**
 * Shared language picker menu used by localized input components.
 * Renders a fallback entry and a list of language entries with customizable suffixes.
 */
export function LocalizedMenu(props: {
    /** Renders the suffix for the fallback entry. */
    renderFallbackSuffix: () => React.ReactNode;
    /** Renders the suffix for a language entry. */
    renderLanguageSuffix: (language: api.TranslationLanguage) => React.ReactNode;
    selectedLanguage: api.TranslationLanguage | null;
    onSelectLanguage: (language: api.TranslationLanguage | null) => void;
    languages: LocalizedLanguagesList;
}) {
    const {
        renderFallbackSuffix,
        renderLanguageSuffix,
        selectedLanguage,
        onSelectLanguage,
        languages,
    } = props;

    return (
        <Menu>
            <MenuItem
                icon={Icon.Globe}
                aria-selected={selectedLanguage === null}
                onAction={() => {
                    onSelectLanguage(null);
                }}
                suffix={
                    <span className="text-ui-small leading-5 tracking-[0.12px] text-neutral-low">
                        {renderFallbackSuffix()}
                    </span>
                }
            >
                Fallback
            </MenuItem>
            {Object.entries(languages).map(([language, descriptor]) => {
                const lang = language as api.TranslationLanguage;

                return (
                    <MenuItem
                        key={language}
                        icon={<Emoji code={descriptor.emoji} />}
                        aria-selected={lang === selectedLanguage}
                        onAction={() => {
                            onSelectLanguage(lang);
                        }}
                        suffix={
                            <span className="text-ui-small leading-5 tracking-[0.12px] text-neutral-low">
                                {renderLanguageSuffix(lang)}
                            </span>
                        }
                    >
                        {descriptor.label}
                    </MenuItem>
                );
            })}
        </Menu>
    );
}
